import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import MagicLinkVerifyPage from "@/pages/MagicLinkVerifyPage";
import MfaChallengePage from "@/pages/MfaChallengePage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import DashboardPage from "@/pages/DashboardPage";
import RosterPage from "@/pages/RosterPage";
import ProfilePage from "@/pages/ProfilePage";
import SignalsPage from "@/pages/SignalsPage";
import CommandShopPage from "@/pages/CommandShopPage";
import TreasuryPage from "@/pages/TreasuryPage";
import JuniorCommandPage from "@/pages/JuniorCommandPage";
import ParentalBridgePage from "@/pages/ParentalBridgePage";
import AuthCallback from "@/pages/AuthCallback";
import SovereignAssistant from "@/components/SovereignAssistant";
import FleetAllianceFooter from "@/components/FleetAllianceFooter";
import { Toaster } from "@/components/ui/sonner";

// ──────── EIGOO Armada · Global Branding Shield ────────
// Every route within the Sovereign Auth hull renders a title of the form
//   "<Vessel Sub-page> | EIGOO Armada"
const ROUTE_TITLES = {
  "/login": "Enter the Armada | EIGOO Armada",
  "/auth/verify": "Magic Link | EIGOO Armada",
  "/auth/mfa-challenge": "MFA Challenge | EIGOO Armada",
  "/auth/forgot-password": "Password Recovery | EIGOO Armada",
  "/auth/reset-password": "Password Reset | EIGOO Armada",
  "/dashboard": "Sovereign Command | EIGOO Armada",
  "/junior-command": "Junior Command · Heir Vault | EIGOO Armada",
  "/parental-bridge": "Parental Bridge | EIGOO Armada",
  "/profile": "Security & Profile | EIGOO Armada",
  "/security": "Security & MFA | EIGOO Armada",
  "/shop": "Command Shop | EIGOO Armada",
  "/treasury": "Fleet Treasury | EIGOO Armada",
  "/roster": "Dynasty Roster | EIGOO Armada",
  "/signals": "Armada Signals | EIGOO Armada",
};

function useDocumentTitle() {
  const location = useLocation();
  useEffect(() => {
    document.title = ROUTE_TITLES[location.pathname] || "Sovereign Auth | EIGOO Armada";
  }, [location.pathname]);
}

function AppRouter() {
  const location = useLocation();
  useDocumentTitle();
  // CRITICAL: detect OAuth return synchronously (NOT in useEffect)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<MagicLinkVerifyPage />} />
      <Route path="/auth/mfa-challenge" element={<MfaChallengePage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/junior-command" element={<ProtectedRoute allowedRoles={["junior_admiral", "lineage_kin", "sovereign"]}><JuniorCommandPage /></ProtectedRoute>} />
      <Route path="/parental-bridge" element={<ProtectedRoute allowedRoles={["guardian", "sovereign"]}><ParentalBridgePage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><CommandShopPage /></ProtectedRoute>} />
      <Route path="/treasury" element={<ProtectedRoute requireSovereign><TreasuryPage /></ProtectedRoute>} />
      <Route path="/roster" element={<ProtectedRoute requireSovereign><RosterPage /></ProtectedRoute>} />
      <Route path="/signals" element={<ProtectedRoute requireSovereign><SignalsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <FleetAllianceFooter />
          <SovereignAssistant />
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
